import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../apiClient';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './PortfolioValueChart.css';

const PortfolioValueChart = ({ properties, selectedPropertyIds }) => {
  const { instance } = useMsal();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track properties changing to trigger refetch
  const propertiesVersion = properties.map(p => p.latestValue).join(',');

  useEffect(() => {
    if (selectedPropertyIds.length > 0) {
      fetchAllValueData();
    } else {
      setChartData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyIds, propertiesVersion]);

  const fetchAllValueData = async () => {
    try {
      setLoading(true);
      setError(null);

      const ids = selectedPropertyIds.join(',');
      const data = await apiFetchJson(instance, `/api/PropertiesDev/portfolio-chart?propertyIds=${ids}`);
      const propList = Array.isArray(data.properties) ? data.properties : (data.properties?.$values || []);
      const points = Array.isArray(data.dataPoints) ? data.dataPoints : (data.dataPoints?.$values || []);

      const combined = points.map(pt => {
        const d = new Date(pt.date);
        const dataPoint = {
          date: d.toLocaleDateString('nb-NO', { year: 'numeric', month: 'short', day: 'numeric' }),
          timestamp: d.getTime(),
          fullDate: d,
          total: pt.total || 0
        };
        // Map property IDs to names for chart lines
        propList.forEach(p => {
          dataPoint[p.name] = pt.values?.[p.id] ?? null;
        });
        return dataPoint;
      });

      setChartData(combined);
    } catch (err) {
      console.error('Error in fetchAllValueData:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M kr`;
    }
    return `${(value / 1000).toFixed(0)}k kr`;
  };

  const getPropertyColor = (index) => {
    const colors = ['#0d9488', '#0284c7', '#dc2626', '#ea580c', '#7c3aed', '#db2777'];
    return colors[index % colors.length];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="portfolio-chart-tooltip">
          <p className="tooltip-date">{label}</p>
          {payload.map((entry, index) => (
            entry.value != null ? (
              <p key={index} className="tooltip-value" style={{ color: entry.color }}>
                {entry.name}: {entry.value.toLocaleString('nb-NO')} kr
              </p>
            ) : null
          ))}
        </div>
      );
    }
    return null;
  };

  if (selectedPropertyIds.length === 0) {
    return (
      <div className="portfolio-chart-section">
        <div className="chart-header">
          <h2>📊 Portfolio Value History</h2>
        </div>
        <div className="empty-chart">
          <p>Select one or more properties above to view their value history</p>
          <p className="hint">Click the checkboxes on property cards to compare values over time</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="portfolio-chart-section">
        <div className="chart-header">
          <h2>📊 Portfolio Value History</h2>
        </div>
        <div className="loading">Loading value data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portfolio-chart-section">
        <div className="chart-header">
          <h2>📊 Portfolio Value History</h2>
        </div>
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="portfolio-chart-section">
        <div className="chart-header">
          <h2>📊 Portfolio Value History</h2>
        </div>
        <div className="empty-chart">
          <p>No value history available for selected properties</p>
          <p className="hint">Add value snapshots using "📈 Update Value" on property cards</p>
        </div>
      </div>
    );
  }

  // Calculate summary stats
  const selectedProperties = properties.filter(p => selectedPropertyIds.includes(p.id));
  const totalPurchasePrice = selectedProperties.reduce((sum, p) => sum + (p.purchasePrice || 0), 0);
  const currentTotal = chartData.length > 0 ? chartData[chartData.length - 1].total : 0;
  const totalGain = currentTotal - totalPurchasePrice;
  const totalGainPercent = totalPurchasePrice > 0 ? ((totalGain / totalPurchasePrice) * 100).toFixed(1) : 0;

  // Read theme-aware colors for SVG chart axes
  const styles = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
  const axisStroke = styles?.getPropertyValue('--text-muted').trim() || '#515a68';
  const lineStroke = styles?.getPropertyValue('--text-primary').trim() || '#12161c';

  return (
    <div className="portfolio-chart-section">
      <div className="chart-header">
        <h2>📊 Portfolio Value History</h2>
        <p className="chart-subtitle">
          {selectedPropertyIds.length === 1 
            ? '1 property selected' 
            : `${selectedPropertyIds.length} properties selected`}
        </p>
      </div>

      <div className="portfolio-stats">
        <div className="stat-card">
          <div className="stat-label">Purchase Price</div>
          <div className="stat-value">{totalPurchasePrice.toLocaleString('nb-NO')} kr</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Current Value</div>
          <div className="stat-value current">{currentTotal.toLocaleString('nb-NO')} kr</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Gain</div>
          <div className={`stat-value ${totalGain >= 0 ? 'positive' : 'negative'}`}>
            {totalGain >= 0 ? '+' : ''}{totalGain.toLocaleString('nb-NO')} kr
            <span className="percent">({totalGainPercent >= 0 ? '+' : ''}{totalGainPercent}%)</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Data Points</div>
          <div className="stat-value">{chartData.length}</div>
        </div>
      </div>

      <div className="chart-container">
        <h3 className="chart-title">Individual Properties</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-border)" />
            <XAxis 
              dataKey="date"
              stroke={axisStroke}
              tick={{ fontSize: 12 }}
              interval="preserveStartEnd"
            />
            <YAxis 
              stroke={axisStroke}
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrency}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />
            {selectedProperties.map((property, index) => (
              <Line
                key={property.id}
                type="monotone"
                dataKey={property.name}
                stroke={getPropertyColor(index)}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                name={property.name}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {selectedPropertyIds.length > 1 && (
        <div className="chart-container">
          <h3 className="chart-title">Total Portfolio</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-border)" />
              <XAxis 
                dataKey="date"
                stroke={axisStroke}
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                stroke={axisStroke}
                tick={{ fontSize: 12 }}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="total"
                stroke={lineStroke}
                strokeWidth={3}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
                name="Total Portfolio"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default PortfolioValueChart;
